import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import TimelineIcon from "@material-ui/icons/Timeline";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ShowChartIcon from "@material-ui/icons/ShowChart";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    height: 350,
  },
  timeframeToggle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(3),
  },
});

const hebrewLabels = {
  charts: {
    salesOverTime: "מכירות לאורך זמן",
    priceHistory: "היסטוריית מחירים",
    multiMetric: "מדדי ביצועים משולבים",
  },
  timeframes: {
    daily: "יומי",
    weekly: "שבועי",
    monthly: "חודשי",
  },
  metrics: {
    orders: "הזמנות",
    units: "יחידות נמכרו",
    revenue: "הכנסות (₪)",
    avgPrice: "מחיר ממוצע",
    minPrice: "מחיר מינימום",
    maxPrice: "מחיר מקסימום",
    impressions: "חשיפות",
    addToCart: "הוספות לסל",
    conversions: "המרות",
  },
  loading: "טוען נתונים...",
  error: "שגיאה בטעינת הנתונים",
  noData: "אין נתונים זמינים לתקופה זו",
};

class ProductPerformanceCharts extends React.Component {
  _isMounted = false;

  state = {
    loading: true,
    error: null,
    salesData: null,
    priceData: null,
    metricsData: null,
    salesTimeframe: "monthly",
    priceTimeframe: "monthly",
    metricsTimeframe: "monthly",
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadChartData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product?.ean !== this.props.product?.ean) {
      this.loadChartData();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadChartData = async () => {
    const { product } = this.props;
    if (!product?.ean) return;

    this.setState({ loading: true, error: null });

    try {
      // Calculate date range (last 12 months)
      const to = new Date();
      const from = new Date();
      from.setMonth(from.getMonth() - 12);

      const fromStr = from.toISOString().split("T")[0];
      const toStr = to.toISOString().split("T")[0];
      const adminHeaders = utils.getAdminAuthHeaders();

      // Fetch all chart data in parallel
      const [salesRes, priceRes, metricsRes] = await Promise.all([
        fetch(
          `${basePath}/api/v1/admin/product-analytics/${product.ean}/metrics?from=${fromStr}&to=${toStr}&bucket=monthly`,
          { headers: adminHeaders }
        ),
        fetch(
          `${basePath}/api/v1/admin/product-analytics/${product.ean}/price-history?from=${fromStr}&to=${toStr}&bucket=monthly`,
          { headers: adminHeaders }
        ),
        fetch(
          `${basePath}/api/v1/admin/product-analytics/${product.ean}/metrics-timeseries?from=${fromStr}&to=${toStr}&bucket=monthly`,
          { headers: adminHeaders }
        ),
      ]);

      if (!salesRes.ok || !priceRes.ok || !metricsRes.ok) {
        throw new Error("Failed to fetch chart data");
      }

      const [salesData, priceData, metricsData] = await Promise.all([
        salesRes.json(),
        priceRes.json(),
        metricsRes.json(),
      ]);

      // Only update state if component is still mounted
      if (this._isMounted) {
        this.setState({
          loading: false,
          salesData,
          priceData,
          metricsData,
        });
      }
    } catch (error) {
      console.error("Error loading chart data:", error);
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: error.message,
        });
      }
    }
  };

  handleTimeframeChange = (chartType) => (event, newTimeframe) => {
    if (newTimeframe !== null) {
      this.setState({ [chartType]: newTimeframe }, () => {
        this.loadChartDataWithTimeframe(chartType, newTimeframe);
      });
    }
  };

  loadChartDataWithTimeframe = async (chartType, timeframe) => {
    const { product } = this.props;
    if (!product?.ean) return;

    try {
      const to = new Date();
      const from = new Date();
      from.setMonth(from.getMonth() - 12);

      const fromStr = from.toISOString().split("T")[0];
      const toStr = to.toISOString().split("T")[0];

      let endpoint;
      let stateKey;

      if (chartType === "salesTimeframe") {
        endpoint = `${basePath}/api/v1/admin/product-analytics/${product.ean}/metrics?from=${fromStr}&to=${toStr}&bucket=${timeframe}`;
        stateKey = "salesData";
      } else if (chartType === "priceTimeframe") {
        endpoint = `${basePath}/api/v1/admin/product-analytics/${product.ean}/price-history?from=${fromStr}&to=${toStr}&bucket=${timeframe}`;
        stateKey = "priceData";
      } else if (chartType === "metricsTimeframe") {
        endpoint = `${basePath}/api/v1/admin/product-analytics/${product.ean}/metrics-timeseries?from=${fromStr}&to=${toStr}&bucket=${timeframe}`;
        stateKey = "metricsData";
      }

      const res = await fetch(endpoint, { headers: utils.getAdminAuthHeaders() });
      if (!res.ok) throw new Error("Failed to fetch data");

      const data = await res.json();
      
      if (this._isMounted) {
        this.setState({ [stateKey]: data });
      }
    } catch (error) {
      console.error("Error loading chart data:", error);
    }
  };

  formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().substr(-2);
    return `${month}/${year}`;
  };

  formatCurrency = (value) => {
    return `₪${value.toLocaleString()}`;
  };

  formatNumber = (value) => {
    return value.toLocaleString();
  };

  renderSalesChart() {
    const { classes } = this.props;
    const { salesData, salesTimeframe } = this.state;

    if (!salesData || !salesData.timeSeries || !Array.isArray(salesData.timeSeries) || salesData.timeSeries.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noData}
        </Typography>
      );
    }

    const chartData = salesData.timeSeries.map((item) => ({
      date: this.formatDate(item.date),
      orders: item.orderCount || 0,
      units: item.quantity || 0,
      revenue: item.revenue || 0,
    }));

    return (
      <Paper className={classes.paper}>
        <Box className={classes.sectionHeader}>
          <ShowChartIcon color="primary" />
          <Typography variant="h6">{hebrewLabels.charts.salesOverTime}</Typography>
        </Box>

        <FormControl size="small" className={classes.timeframeToggle}>
          <Select
            value={salesTimeframe}
            onChange={(e) => this.handleTimeframeChange("salesTimeframe")(null, e.target.value)}
          >
            <MenuItem value="daily">{hebrewLabels.timeframes.daily}</MenuItem>
            <MenuItem value="weekly">{hebrewLabels.timeframes.weekly}</MenuItem>
            <MenuItem value="monthly">{hebrewLabels.timeframes.monthly}</MenuItem>
          </Select>
        </FormControl>

        <Divider />

        <Box className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip formatter={(value, name) => {
                if (name === hebrewLabels.metrics.revenue) {
                  return this.formatCurrency(value);
                }
                return this.formatNumber(value);
              }} />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="orders"
                name={hebrewLabels.metrics.orders}
                fill="#1976d2"
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="units"
                name={hebrewLabels.metrics.units}
                stroke="#2e7d32"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                name={hebrewLabels.metrics.revenue}
                stroke="#ed6c02"
                strokeWidth={2}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  }

  renderPriceChart() {
    const { classes } = this.props;
    const { priceData, priceTimeframe } = this.state;

    if (!priceData || !priceData.timeSeries || priceData.timeSeries.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noData}
        </Typography>
      );
    }

    const chartData = priceData.timeSeries.map((item) => ({
      date: this.formatDate(item.date),
      avgPrice: item.avgPrice || 0,
      minPrice: item.minPrice || 0,
      maxPrice: item.maxPrice || 0,
    }));

    return (
      <Paper className={classes.paper}>
        <Box className={classes.sectionHeader}>
          <AttachMoneyIcon color="primary" />
          <Typography variant="h6">{hebrewLabels.charts.priceHistory}</Typography>
        </Box>

        <FormControl size="small" className={classes.timeframeToggle}>
          <Select
            value={priceTimeframe}
            onChange={(e) => this.handleTimeframeChange("priceTimeframe")(null, e.target.value)}
          >
            <MenuItem value="daily">{hebrewLabels.timeframes.daily}</MenuItem>
            <MenuItem value="weekly">{hebrewLabels.timeframes.weekly}</MenuItem>
            <MenuItem value="monthly">{hebrewLabels.timeframes.monthly}</MenuItem>
          </Select>
        </FormControl>

        <Divider />

        <Box className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => this.formatCurrency(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="avgPrice"
                name={hebrewLabels.metrics.avgPrice}
                stroke="#1976d2"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="minPrice"
                name={hebrewLabels.metrics.minPrice}
                stroke="#2e7d32"
                strokeWidth={1}
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="maxPrice"
                name={hebrewLabels.metrics.maxPrice}
                stroke="#d32f2f"
                strokeWidth={1}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  }

  renderMetricsChart() {
    const { classes } = this.props;
    const { metricsData, metricsTimeframe } = this.state;

    if (!metricsData || !metricsData.timeSeries || !Array.isArray(metricsData.timeSeries) || metricsData.timeSeries.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noData}
        </Typography>
      );
    }

    const chartData = metricsData.timeSeries.map((item) => ({
      date: this.formatDate(item.date),
      impressions: item.impressions || 0,
      addToCart: item.addToCart || 0,
      orders: item.orders || 0,
    }));

    return (
      <Paper className={classes.paper}>
        <Box className={classes.sectionHeader}>
          <TimelineIcon color="primary" />
          <Typography variant="h6">{hebrewLabels.charts.multiMetric}</Typography>
        </Box>

        <FormControl size="small" className={classes.timeframeToggle}>
          <Select
            value={metricsTimeframe}
            onChange={(e) => this.handleTimeframeChange("metricsTimeframe")(null, e.target.value)}
          >
            <MenuItem value="daily">{hebrewLabels.timeframes.daily}</MenuItem>
            <MenuItem value="weekly">{hebrewLabels.timeframes.weekly}</MenuItem>
            <MenuItem value="monthly">{hebrewLabels.timeframes.monthly}</MenuItem>
          </Select>
        </FormControl>

        <Divider />

        <Box className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => this.formatNumber(value)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="impressions"
                name={hebrewLabels.metrics.impressions}
                stroke="#1976d2"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="addToCart"
                name={hebrewLabels.metrics.addToCart}
                stroke="#2e7d32"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="orders"
                name={hebrewLabels.metrics.conversions}
                stroke="#9c27b0"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  }

  render() {
    const { classes, product } = this.props;
    const { loading, error } = this.state;

    if (!product) return null;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Typography className={classes.errorText}>
          {hebrewLabels.error}: {error}
        </Typography>
      );
    }

    return (
      <div className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {this.renderSalesChart()}
          </Grid>
          <Grid item xs={12}>
            {this.renderPriceChart()}
          </Grid>
          <Grid item xs={12}>
            {this.renderMetricsChart()}
          </Grid>
        </Grid>
      </div>
    );
  }
}

ProductPerformanceCharts.propTypes = {
  classes: PropTypes.object.isRequired,
  product: PropTypes.object,
};

export default withStyles(styles)(ProductPerformanceCharts);
